import axios, { type AxiosInstance, type AxiosResponse } from 'axios'
import type { ApiResponse } from '@/types'
import { secureStorage } from '@/utils/secureStorage'

// Create axios instance with base configuration
const api: AxiosInstance = axios.create({
  baseURL: (import.meta.env.VITE_API_BASE_URL || 'https://portal.boenkop.be') + '/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000, // 10 seconds
  withCredentials: true, // Required for cookie-based CSRF protection
})

// Request interceptor for token-based authentication
api.interceptors.request.use(
  (config) => {
    // Add Bearer token if available
    const token = secureStorage.getToken()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // Add XSRF token from cookie for CSRF protection
    const xsrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1]

    if (xsrfToken) {
      config.headers['X-XSRF-TOKEN'] = decodeURIComponent(xsrfToken)
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response
  },
  (error) => {
    // HTTP errors (401/403/5xx) are handled by the auth store, router guards,
    // and individual components — just propagate them.
    return Promise.reject(error)
  }
)

export const useApi = () => {
  const get = async <T = any>(url: string, params?: Record<string, any>): Promise<ApiResponse<T>> => {
    const response = await api.get<ApiResponse<T>>(url, { params })
    return response.data
  }
  
  const post = async <T = any>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    const response = await api.post<ApiResponse<T>>(url, data)
    return response.data
  }
  
  const put = async <T = any>(url: string, data?: unknown): Promise<ApiResponse<T>> => {
    const response = await api.put<ApiResponse<T>>(url, data)
    return response.data
  }
  
  const deleteRequest = async <T = any>(url: string): Promise<ApiResponse<T>> => {
    const response = await api.delete<ApiResponse<T>>(url)
    return response.data
  }
  
  return {
    get,
    post,
    put,
    delete: deleteRequest,
    instance: api
  }
}

// Export the axios instance for direct use if needed
export { api }
export default api